// src/pages/Dashboard/Admin/PagesPersonnalisees/components/PagesTable.jsx

import React from "react";
import {
  FaEdit,
  FaExternalLinkAlt,
  FaFileAlt,
  FaLayerGroup,
  FaNewspaper,
  FaTrash,
} from "react-icons/fa";

import {
  formatDateFR,
  getPageArticlesCount,
  getPageIntroduction,
  getPageSlug,
  getPageTitle,
  getPageUpdatedAt,
  isPageActive,
  truncateText,
} from "../utils/pagesDashboardUtils";

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaFileAlt />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucune page trouvée
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Modifiez les filtres ou créez une nouvelle page personnalisée.
        </p>
      </div>
    </section>
  );
}

function ActionButton({ title, onClick, disabled, children, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    info: "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    violet:
      "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.base
      }`}
    >
      {children}
    </button>
  );
}

export default function PagesTable({
  pages,
  loading,
  deletingId,
  onEdit,
  onDelete,
  onManageArticles,
}) {
  if (loading) return <SkeletonRows />;
  if (!pages.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Pages CMS
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Liste des pages personnalisées
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Gérez le contenu libre du site : statut public, chemin de page,
            introduction et articles associés.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1060px] table-fixed">
            <colgroup>
              <col className="w-[112px]" />
              <col className="w-[290px]" />
              <col className="w-[230px]" />
              <col className="w-[260px]" />
              <col className="w-[95px]" />
              <col className="w-[95px]" />
              <col className="w-[150px]" />
            </colgroup>

            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="sticky left-0 z-30 border-r border-slate-200 bg-slate-50 px-3 py-3 text-left font-bold dark:border-slate-700 dark:bg-slate-950">
                  Statut
                </th>
                <th className="px-3 py-3 text-left font-bold">Page</th>
                <th className="px-3 py-3 text-left font-bold">Chemin</th>
                <th className="px-3 py-3 text-left font-bold">Introduction</th>
                <th className="px-3 py-3 text-center font-bold">Articles</th>
                <th className="px-3 py-3 text-left font-bold">MAJ</th>
                <th className="px-3 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-white/60 text-sm dark:divide-slate-800">
              {pages.map((page) => {
                const title = getPageTitle(page);
                const slug = getPageSlug(page);
                const active = isPageActive(page);
                const intro = truncateText(getPageIntroduction(page), 150);
                const count = getPageArticlesCount(page);

                return (
                  <tr
                    key={page.id}
                    className={`transition ${
                      active
                        ? "bg-emerald-50/40 hover:bg-emerald-100/50 dark:bg-emerald-500/5 dark:hover:bg-emerald-500/10"
                        : "bg-slate-50/80 hover:bg-slate-100/80 dark:bg-slate-900 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <td
                      className={`sticky left-0 z-20 border-r border-slate-200 px-3 py-4 align-top dark:border-slate-700 ${
                        active
                          ? "bg-emerald-50 dark:bg-slate-900"
                          : "bg-slate-50 dark:bg-slate-900"
                      }`}
                    >
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${
                          active
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                            : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                        }`}
                      >
                        {active ? "Actif" : "Inactif"}
                      </span>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <p className="line-clamp-2 font-bold text-slate-900 dark:text-white">
                        {title}
                      </p>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="space-y-2">
                        <span className="inline-flex max-w-full rounded-2xl bg-white px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700">
                          <span className="truncate">/page/{slug || "—"}</span>
                        </span>

                        {slug ? (
                          <a
                            href={`/page/${slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-bold text-indigo-700 underline underline-offset-2 hover:opacity-80 dark:text-indigo-300"
                          >
                            <FaExternalLinkAlt className="text-[10px]" />
                            Voir la page
                          </a>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-3 py-4 align-top">
                      {intro ? (
                        <p
                          className="line-clamp-3 text-xs leading-5 text-slate-600 dark:text-slate-300"
                          title={intro}
                        >
                          {intro}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                          Aucune introduction
                        </p>
                      )}
                    </td>

                    <td className="px-3 py-4 text-center align-top">
                      <button
                        type="button"
                        onClick={() => onManageArticles(page)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-black text-slate-900 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-white dark:ring-slate-700 dark:hover:bg-slate-800"
                      >
                        <FaNewspaper className="text-violet-600 dark:text-violet-300" />
                        {count}
                      </button>
                    </td>

                    <td className="px-3 py-4 align-top text-xs font-medium text-slate-600 dark:text-slate-300">
                      {formatDateFR(getPageUpdatedAt(page))}
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="flex items-center justify-end gap-1.5">
                        <ActionButton
                          title="Modifier"
                          onClick={() => onEdit(page)}
                          tone="primary"
                        >
                          <FaEdit />
                        </ActionButton>

                        <ActionButton
                          title="Gérer les articles"
                          onClick={() => onManageArticles(page)}
                          tone="violet"
                        >
                          <FaLayerGroup />
                        </ActionButton>

                        {slug ? (
                          <a
                            href={`/page/${slug}`}
                            target="_blank"
                            rel="noreferrer"
                            title="Ouvrir la page publique"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-700 transition hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        ) : null}

                        <ActionButton
                          title="Supprimer"
                          onClick={() => onDelete(page)}
                          disabled={deletingId === page.id}
                          tone="danger"
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}